import type { Member } from '@stytch/nextjs/b2b'
import { CLIENT_PAGES } from '@/constants/clientPortalPages'
import type { IdentityInfoSchemaType } from '@/types/portal-api.generated'
import { clearAuthUserId } from '@/platform/http'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { clearAllAnalyticsQuestionHistory } from '@/utilities/client/analyticsQuestionHistoryStorage'
import type { PostAuthDependencies } from '@/utilities/auth/authHelper'
import { finishAuthenticatedSession } from '@/utilities/auth/finishAuthenticatedSession'
import { ClientIdentityRequiredError } from '@/utilities/auth/runClientPostAuth'
import {
  getCachedTrustedIdentityScopes,
  restoreCachedTrustedIdentityScopes,
} from '@/utilities/auth/trustedIdentityScopes'
import {
  emitSelectedClientIdChanged,
  getSelectedClientId,
  removeSelectedClientId,
  setSelectedClientId,
} from '@/utilities/client/selectedClientIdStorage'
import {
  clearPartnersCache,
  getPartnersCache,
  setPartnersCache,
  type CachedPartner,
} from '@/utilities/partnersCache'
import { STYTCH_ACTIVE_SESSION_DURATION } from '@/utilities/stytch/config'
import { getStytchSessionTokens } from '@/utilities/stytch/getStytchJWT'
import { IMPERSONATE_FAILED_MESSAGE } from '@/utilities/stytch/impersonationError'
import {
  awaitImpersonationHandoffMember,
  beginImpersonationHandoff,
  endImpersonationHandoff,
  getImpersonationHandoffExpectedMemberId,
  isImpersonationHandoffInProgress,
} from '@/utilities/stytch/impersonationHandoff'
import { clearStytchCookies } from '@/utilities/stytch/stytchClient'
import { resetAllInMemoryFilterCommits } from '@/utilities/orders/inMemoryFilterCommit'

/**
 * Shared Client Portal impersonation session handoff.
 *
 * Both the Stytch-token authenticate route and the Users-table impersonate action
 * drop predecessor partner/identity caches, then run `finishAuthenticatedSession`
 * with signup disabled so the impersonated session keeps the values post-auth
 * just wrote. Users-table credentials hydrate the SDK via `session.updateSession`
 * so cookie policy stays in Stytch.
 *
 * Do not revoke the predecessor Stytch session before the impersonated session
 * authenticates. Root `SessionMonitor` treats `member` going null as expiry and
 * would send the admin to login mid-handoff. On Users-table failure, restore
 * the predecessor tokens and Client Portal identity instead of leaving the
 * admin logged out. Keep the handoff open until `useStytchMember` matches the
 * restored or impersonated member so a lagged hook update cannot look like expiry.
 * Logout and a fail-safe timeout call `endImpersonationHandoff()` so a stuck
 * handoff cannot suppress session expiry for the rest of the SPA session.
 */

export type ImpersonationSessionTokens = {
  session_jwt: string
  session_token: string
}

type PredecessorClientState = {
  selectedClientId: string | null
  trustedScopes: IdentityInfoSchemaType['scopes'] | null
  partners: CachedPartner[] | null
}

export type ImpersonationRevokeClient = {
  session: {
    revoke: () => Promise<unknown>
    updateSession?: (tokens: ImpersonationSessionTokens) => void
    authenticate?: (options?: {
      session_duration_minutes: number
    }) => Promise<{ member?: Member | null }>
    getTokens?: () => {
      session_jwt?: string | null
      session_token?: string | null
    } | null
  }
}

export type ImpersonationSessionClient = ImpersonationRevokeClient & {
  session: ImpersonationRevokeClient['session'] & {
    updateSession: (tokens: ImpersonationSessionTokens) => void
    authenticate: (options?: {
      session_duration_minutes: number
    }) => Promise<{ member?: Member | null }>
  }
}

function readPredecessorClientState(): PredecessorClientState {
  const partners = getPartnersCache()
  return {
    selectedClientId: getSelectedClientId(),
    trustedScopes: getCachedTrustedIdentityScopes(),
    partners: partners ? partners.map((partner) => ({ ...partner })) : null,
  }
}

function applyPredecessorClientState(
  state: PredecessorClientState,
  member: Member | null | undefined,
  postAuthDeps?: PostAuthDependencies,
): void {
  if (state.selectedClientId) {
    setSelectedClientId(state.selectedClientId)
    emitSelectedClientIdChanged(state.selectedClientId)
  } else {
    removeSelectedClientId()
    emitSelectedClientIdChanged(null)
  }
  if (state.partners) {
    setPartnersCache(state.partners)
  } else {
    clearPartnersCache()
  }
  restoreCachedTrustedIdentityScopes(state.trustedScopes)

  if (member && postAuthDeps) {
    postAuthDeps.loginUser(member)
    postAuthDeps.identifyUser(member)
  }
}

function readPredecessorSessionTokens(
  stytchClient: ImpersonationRevokeClient,
): ImpersonationSessionTokens | null {
  const fromSdk = stytchClient.session.getTokens?.()
  const sdkJwt = fromSdk?.session_jwt?.trim() ?? ''
  const sdkToken = fromSdk?.session_token?.trim() ?? ''
  if (sdkJwt && sdkToken) {
    return { session_jwt: sdkJwt, session_token: sdkToken }
  }

  const fromCookies = getStytchSessionTokens()
  if (!fromCookies) return null
  return {
    session_jwt: fromCookies.sessionJwt,
    session_token: fromCookies.sessionToken,
  }
}

async function restorePredecessorSession(args: {
  stytchClient: ImpersonationRevokeClient
  predecessorTokens: ImpersonationSessionTokens | null
  predecessorClientState: PredecessorClientState
  orgSlug: string
  postAuthDeps?: PostAuthDependencies
}): Promise<void> {
  const { stytchClient, predecessorTokens, predecessorClientState, orgSlug, postAuthDeps } = args
  if (predecessorTokens && stytchClient.session.updateSession) {
    clearAllAnalyticsQuestionHistory()
    stytchClient.session.updateSession(predecessorTokens)
    try {
      const response = await stytchClient.session.authenticate?.({
        session_duration_minutes: STYTCH_ACTIVE_SESSION_DURATION,
      })
      const restoredMember = response?.member
      const restoredMemberId = restoredMember?.member_id?.trim() ?? ''
      if (restoredMemberId) {
        applyPredecessorClientState(predecessorClientState, restoredMember, postAuthDeps)
        awaitImpersonationHandoffMember(restoredMemberId)
        return
      }
    } catch (err) {
      trackEvent('impersonation:predecessor_restore_fail', {
        ...errorAnalyticsPayload(err),
        orgSlug,
      })
    }
  }

  await revokeAndClearStytchSession({
    stytchClient,
    expectSession: true,
    orgSlug,
  })
  endImpersonationHandoff()
}

export async function revokeAndClearStytchSession(args: {
  stytchClient: ImpersonationRevokeClient | null | undefined
  expectSession?: boolean
  orgSlug?: string
}): Promise<void> {
  const { stytchClient, expectSession = false, orgSlug } = args
  // Clear free-text history before the async revoke, even if the SDK is unavailable or fails.
  clearAllAnalyticsQuestionHistory()
  if (!stytchClient) {
    return
  }

  try {
    await stytchClient.session.revoke()
  } catch (err) {
    if (expectSession) {
      trackEvent('impersonation:revoke_fail', {
        ...errorAnalyticsPayload(err),
        orgSlug,
      })
    }
  }

  clearStytchCookies()
  resetAllInMemoryFilterCommits()
}

export function clearPredecessorClientSessionState(): void {
  clearAllAnalyticsQuestionHistory()
  clearAuthUserId()
  removeSelectedClientId()
  clearPartnersCache()
  resetAllInMemoryFilterCommits()
}

export async function finishImpersonatedClientSession(args: {
  member: Member
  orgSlug: string
  isClient: boolean
  postAuthDeps: PostAuthDependencies
  searchParams: URLSearchParams
  stytchClient: ImpersonationRevokeClient
  ignoreRedirectIntent?: boolean
  predecessorTokens?: ImpersonationSessionTokens | null
  predecessorClientState?: PredecessorClientState
}): Promise<string> {
  const {
    member,
    orgSlug,
    isClient,
    postAuthDeps,
    searchParams,
    stytchClient,
    ignoreRedirectIntent = false,
    predecessorTokens = null,
    predecessorClientState = readPredecessorClientState(),
  } = args

  try {
    // Drop the predecessor admin caches first so post-auth can persist the
    // impersonated partner/identity through reload or client-side navigation.
    clearPredecessorClientSessionState()
    const dest = await finishAuthenticatedSession(
      member,
      orgSlug,
      searchParams,
      isClient,
      postAuthDeps,
      {
        allowClientSignup: false,
      },
    )
    trackEvent('impersonation:session_established', {
      memberId: member.member_id,
      email: member.email_address,
      orgId: member.organization_id,
      orgSlug,
    })
    return ignoreRedirectIntent ? CLIENT_PAGES.dashboard : dest
  } catch (err) {
    if (err instanceof ClientIdentityRequiredError) {
      trackEvent('impersonation:no_existing_identity', {
        memberId: member.member_id,
        orgSlug,
      })
      await restorePredecessorSession({
        stytchClient,
        predecessorTokens,
        predecessorClientState,
        orgSlug,
        postAuthDeps,
      })
      throw err
    }

    trackEvent('login:post_auth_fail', {
      ...errorAnalyticsPayload(err),
    })
    await restorePredecessorSession({
      stytchClient,
      predecessorTokens,
      predecessorClientState,
      orgSlug,
      postAuthDeps,
    })
    throw err
  }
}

export async function establishImpersonatedClientSessionFromTokens(args: {
  stytchClient: ImpersonationSessionClient
  sessionJwt: string
  sessionToken: string
  orgSlug: string
  postAuthDeps: PostAuthDependencies
}): Promise<string> {
  const { stytchClient, sessionJwt, sessionToken, orgSlug, postAuthDeps } = args
  const predecessorTokens = readPredecessorSessionTokens(stytchClient)
  const predecessorClientState = readPredecessorClientState()

  beginImpersonationHandoff()
  try {
    // Authentication can fail before post-auth cleanup runs; discard history before replacing tokens.
    clearAllAnalyticsQuestionHistory()
    stytchClient.session.updateSession({
      session_jwt: sessionJwt,
      session_token: sessionToken,
    })

    let member: Member | null | undefined
    try {
      // This length stays for the impersonated session.
      // ClientPortalSessionLengthEffect does not replace it with the partner flag.
      const response = await stytchClient.session.authenticate({
        session_duration_minutes: STYTCH_ACTIVE_SESSION_DURATION,
      })
      member = response?.member
    } catch (err) {
      await restorePredecessorSession({
        stytchClient,
        predecessorTokens,
        predecessorClientState,
        orgSlug,
        postAuthDeps,
      })
      throw err
    }

    if (!member) {
      await restorePredecessorSession({
        stytchClient,
        predecessorTokens,
        predecessorClientState,
        orgSlug,
        postAuthDeps,
      })
      throw new Error(IMPERSONATE_FAILED_MESSAGE)
    }

    const dest = await finishImpersonatedClientSession({
      member,
      orgSlug,
      isClient: true,
      postAuthDeps,
      searchParams: new URLSearchParams(),
      stytchClient,
      ignoreRedirectIntent: true,
      predecessorTokens,
      predecessorClientState,
    })
    awaitImpersonationHandoffMember(member.member_id)
    return dest
  } finally {
    // Restore/revoke already settled the handoff. Only force-idle if work aborted
    // before a restore or successful authenticate could publish an expected member.
    if (isImpersonationHandoffInProgress() && !getImpersonationHandoffExpectedMemberId()) {
      endImpersonationHandoff()
    }
  }
}
